import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Divider,
    Grid2,
    IconButton,
    Skeleton,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StyleIcon from '@mui/icons-material/Style';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChairIcon from '@mui/icons-material/Chair';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PowerIcon from '@mui/icons-material/Power';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import { Link, useNavigate } from 'react-router-dom';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import useNotify from '../../../hooks/useNotify';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { useTranslation } from 'react-i18next';
import { deleteShow } from '@openapi/shows/shows';
import { getFullImageUrl } from '../../../helpers/imageUtils';

const CARD_IMAGE_HEIGHT = 160;

const ShowCard = ({ show, onDelete, deletingId }) => {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCardClick = () => {
        if (canViewShows) {
            navigate(`/shows/edit/${show.id}`);
        }
    };

    const canViewShows = can(CAPABILITIES.SHOWS_VIEW);
    const canManageShows = can(CAPABILITIES.SHOWS_MANAGE);
    const canViewCoupons = can(CAPABILITIES.COUPONS_VIEW);
    const canManageIntegrations = can(CAPABILITIES.INTEGRATIONS_MANAGE);
    const canViewCommissionGroups = can(CAPABILITIES.COMMISSION_GROUPS_VIEW);
    const showImage = show.marketing?.primaryImage || null;

    // Inline footer actions: every action icon except delete sits in the footer.
    // Delete stays in the dropdown to avoid accidental clicks.
    const inlineActions = [
        ...(canViewShows
            ? [
                  {
                      label: t('timeslots.title'),
                      icon: <AccessTimeIcon fontSize="small" />,
                      to: `/shows/${show.id}/timeslots`,
                  },
              ]
            : []),
        ...(canViewCoupons
            ? [
                  {
                      label: t('coupons.title'),
                      icon: <LocalOfferIcon fontSize="small" />,
                      to: `/shows/${show.id}/coupons`,
                  },
              ]
            : []),
        ...(canViewShows
            ? [
                  {
                      label: t('ticketoptions.title'),
                      icon: <StyleIcon fontSize="small" />,
                      to: `/shows/${show.id}/ticketoptions`,
                  },
                  {
                      label: t('tickettypes.title'),
                      icon: <ConfirmationNumberIcon fontSize="small" />,
                      to: `/shows/${show.id}/tickettypes`,
                  },
              ]
            : []),
        ...(canManageShows
            ? [
                  {
                      label: t('seats.title'),
                      icon: <ChairIcon fontSize="small" />,
                      to: `/shows/${show.id}/seats`,
                  },
              ]
            : []),
        ...(canManageIntegrations
            ? [
                  {
                      label: t('integrations.title'),
                      icon: <PowerIcon fontSize="small" />,
                      to: `/shows/${show.id}/integrations`,
                  },
              ]
            : []),
        ...(canViewCommissionGroups
            ? [
                  {
                      label: t('commissionGroups.title'),
                      icon: <GroupWorkIcon fontSize="small" />,
                      to: `/shows/${show.id}/commissiongroups`,
                  },
              ]
            : []),
        ...(show.location
            ? [
                  {
                      label: t('shows.columns.location'),
                      icon: <OpenInNewIcon fontSize="small" />,
                      href: show.location,
                      external: true,
                  },
              ]
            : []),
        ...(canManageShows
            ? [
                  {
                      label: t('common.edit'),
                      icon: <EditIcon fontSize="small" />,
                      to: `/shows/edit/${show.id}`,
                  },
              ]
            : []),
    ];

    const deleteAction = canManageShows
        ? {
              label: t('common.delete'),
              icon: <DeleteIcon fontSize="small" color="error" />,
              onClick: () => {
                  handleMenuClose();
                  onDelete();
              },
              disabled: deletingId === show.id,
              color: 'error',
          }
        : null;

    const handleInlineClick = (action) => {
        if (action.external) {
            window.open(action.href, '_blank');
        } else if (action.to) {
            navigate(action.to);
        }
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: 4,
                },
                border: '1px solid',
                borderColor: 'grey.300',
            }}
        >
            {/* Clickable Area - Image and Content */}
            <CardActionArea onClick={handleCardClick} sx={{ flexGrow: 1 }}>
                {/* Image */}
                <CardMedia
                    component="div"
                    sx={{
                        height: CARD_IMAGE_HEIGHT,
                        bgcolor: 'grey.200',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundImage: showImage ? `url(${getFullImageUrl(showImage)})` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {!showImage && (
                        <ConfirmationNumberIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                    )}
                </CardMedia>

                {/* Content */}
                <CardContent sx={{ pb: 1 }}>
                    <Typography variant="h6" component="h2" noWrap title={show.title}>
                        {show.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {show.venueName}
                    </Typography>
                </CardContent>
            </CardActionArea>

            {/* Actions */}
            <Divider />
            <Box
                sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                }}
            >
                <Stack
                    direction="row"
                    spacing={0.25}
                    alignItems="center"
                    sx={{ flexWrap: 'wrap', rowGap: 0.25 }}
                >
                    {inlineActions.map((action) =>
                        action.external ? (
                            <Tooltip key={action.label} title={action.label}>
                                <IconButton size="small" onClick={() => handleInlineClick(action)}>
                                    {action.icon}
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip key={action.label} title={action.label}>
                                <IconButton size="small" component={Link} to={action.to}>
                                    {action.icon}
                                </IconButton>
                            </Tooltip>
                        ),
                    )}
                </Stack>
                {deleteAction && (
                    <Box sx={{ ml: 'auto' }}>
                        <Tooltip title={t('common.more')}>
                            <IconButton size="small" onClick={handleMenuOpen}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>

            {/* Dropdown Menu — delete only */}
            {deleteAction && (
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={deleteAction.onClick} disabled={deleteAction.disabled}>
                        <ListItemIcon>{deleteAction.icon}</ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>
                            {deleteAction.label}
                        </ListItemText>
                    </MenuItem>
                </Menu>
            )}
        </Card>
    );
};

const ShowCardSkeleton = () => (
    <Card sx={{ height: '100%' }}>
        <Skeleton variant="rectangular" height={CARD_IMAGE_HEIGHT} />
        <CardContent>
            <Skeleton variant="text" width="80%" height={32} />
            <Skeleton variant="text" width="60%" height={20} />
        </CardContent>
        <Divider />
        <Box sx={{ p: 1 }}>
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                </Stack>
                <Skeleton variant="circular" width={32} height={32} />
            </Stack>
        </Box>
    </Card>
);

const ShowsTable = ({ rows, limit, page, setParams, total, loading, reloadData }) => {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [deletingId, setDeletingId] = useState(null);

    const handleDeleteShow = useCallback(
        async (id) => {
            setDeletingId(id);
            try {
                await deleteShow(id);
                notifySuccess(t('shows.notifications.deleted'));
                reloadData();
            } catch (_error) {
                notifyError(t('shows.notifications.deleteError'));
            } finally {
                setDeletingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    const confirmDelete = useCallback(
        (show) => {
            showModal(MODAL_TYPE.CONFIRMATION, {
                title: t('confirmationModal.title'),
                content: t('confirmationModal.content'),
                confirm: () => handleDeleteShow(show.id),
            });
        },
        [handleDeleteShow, showModal, t],
    );

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    return (
        <Grid2 container spacing={3} width="100%">
            {/* Cards Grid */}
            {loading
                ? Array.from({ length: limit }).map((_, index) => (
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                          <ShowCardSkeleton />
                      </Grid2>
                  ))
                : rows?.map((show) => (
                      <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={show.id}>
                          <ShowCard
                              show={show}
                              onDelete={() => confirmDelete(show)}
                              deletingId={deletingId}
                          />
                      </Grid2>
                  ))}

            {/* Empty State */}
            {!loading && (!rows || rows.length === 0) && (
                <Grid2 size={{ xs: 12 }}>
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 8,
                            color: 'text.secondary',
                        }}
                    >
                        <ConfirmationNumberIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6">{t('shows.noShows')}</Typography>
                    </Box>
                </Grid2>
            )}

            {/* Pagination */}
            <Grid2 size={{ xs: 12 }}>
                <TablePagination
                    count={total || 0}
                    rowsPerPage={limit}
                    component="div"
                    page={rows?.length <= 0 ? 0 : page}
                    onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                    rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                    onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                    labelRowsPerPage={t('labelRowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                />
            </Grid2>
        </Grid2>
    );
};

export default ShowsTable;
